import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../Context/useAuth"; // Adjust this import path to match folder structure

interface ToDo {
  id: number;
  text: string;
  isCompleted: boolean;
}

export default function MyToDoPage() {
  const [todos, setTodos] = useState<ToDo[]>([]);
  const [newTodoText, setNewTodoText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState<string>("");

  // State to manage the deletion confirmation modal
  const [todoToDelete, setTodoToDelete] = useState<number | null>(null);

  const { isLoggedIn } = useAuth();
  const isOwner = isLoggedIn();

  useEffect(() => {
    const initialisePage = async () => {
      try {
        const res = await axios.get<ToDo[]>("/api/todo");
        setTodos(res.data);
      } catch (error) {
        console.error("Error connecting to the server:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initialisePage();
  }, []);

  const handleToggleComplete = async (id: number) => {
    if (!isOwner) return;

    setTodos((prev) =>
      prev.map((todo) => (todo.id === id ? { ...todo, isCompleted: !todo.isCompleted } : todo))
    );

    try {
      await axios.put(`/api/todo/${id}/toggle`);
    } catch (error) {
      console.error("Failed to toggle task:", error);
      const freshData = await axios.get<ToDo[]>("/api/todo");
      setTodos(freshData.data);
    }
  };

  const handleAddTodo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner || !newTodoText.trim()) return;

    try {
      const res = await axios.post<ToDo>("/api/todo", { text: newTodoText });
      setTodos((prev) => [...prev, res.data]);
      setNewTodoText("");
    } catch (error) {
      console.error("Failed to create task:", error);
    }
  };

  // 1. Triggers the modal instead of deleting immediately
  const requestDelete = (id: number) => {
    if (!isOwner) return;
    setTodoToDelete(id);
  };

  // 2. Actually performs the deletion if confirmed
  const confirmDelete = async () => {
    if (todoToDelete === null) return;

    try {
      await axios.delete(`/api/todo/${todoToDelete}`);
      setTodos((prev) => prev.filter((todo) => todo.id !== todoToDelete));
    } catch (error) {
      console.error("Failed to delete task:", error);
    } finally {
      // Always close the modal, whether successful or not
      setTodoToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 text-gray-500">Loading tasks...</div>
    );
  }

  const pendingTodos = todos.filter((todo) => !todo.isCompleted);
  const completedTodos = todos.filter((todo) => todo.isCompleted);

  const startEditing = (todo: ToDo) => {
    setEditingId(todo.id);
    setEditText(todo.text);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (id: number) => {
    if (!editText.trim()) return;

    try {
      // Call C# endpoint
      await axios.put(`/api/todo/${id}`, { text: editText });

      // Update the local React state instantly
      setTodos((prev) => prev.map((todo) => (todo.id === id ? { ...todo, text: editText } : todo)));

      // Close the edit mode
      setEditingId(null);
      setEditText("");
    } catch (error) {
      console.error("Failed to update task:", error);
    }
  };

  return (
    // Widened to max-w-5xl to accommodate side-by-side columns
    <div className="max-w-5xl mx-auto p-4">
      <h1 className="text-3xl font-bold mb-2">My To-Do List</h1>
      <p className="text-gray-600 mb-6">A live look at what I'm currently working on.</p>

      {isOwner && (
        <div className="mb-8 p-4 bg-gray-50 border rounded-lg shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-3">Admin Dashboard Active</p>
          <form onSubmit={handleAddTodo} className="flex space-x-2 max-w-2xl">
            <input
              type="text"
              placeholder="What needs to be done?"
              className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={newTodoText}
              onChange={(e) => setNewTodoText(e.target.value)}
            />
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
            >
              Add Task
            </button>
          </form>
        </div>
      )}

      {/* Grid Layout: 1 column on mobile, 2 side-by-side on md+ screens */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* SECTION 1: Tasks To Do */}
        <section className="flex flex-col border rounded-xl bg-white shadow-sm h-[600px]">
          <div className="p-4 border-b bg-gray-50 rounded-t-xl">
            <h2 className="text-lg font-semibold text-gray-800">
              Tasks To Do{" "}
              <span className="text-sm font-normal text-gray-500 ml-1">
                ({pendingTodos.length})
              </span>
            </h2>
          </div>

          {/* overflow-y-auto makes this specific list scrollable */}
          <div className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-3">
              {pendingTodos.length === 0 ? (
                <p className="text-gray-500 italic text-sm text-center mt-4">
                  No pending tasks. You're all caught up!
                </p>
              ) : (
                pendingTodos.map((todo) => (
                  <li
                    key={todo.id}
                    className="flex items-center justify-between p-3 border rounded shadow-sm bg-white hover:border-blue-300 transition-all"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      {isOwner ? (
                        <input
                          type="checkbox"
                          className="h-5 w-5 cursor-pointer text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          checked={todo.isCompleted}
                          onChange={() => handleToggleComplete(todo.id)}
                          disabled={editingId === todo.id} // Prevents toggling while typing
                        />
                      ) : (
                        <span className="text-xl">⏳</span>
                      )}

                      {/* DYNAMIC EDIT BLOCK */}
                      {editingId === todo.id ? (
                        <div className="flex-1 flex space-x-2 mr-2">
                          <input
                            type="text"
                            className="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            autoFocus // Instantly places the cursor in the field
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit(todo.id);
                              if (e.key === "Escape") cancelEditing();
                            }}
                          />
                          <button
                            onClick={() => handleSaveEdit(todo.id)}
                            className="text-green-600 hover:text-green-700 font-medium text-sm transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="text-gray-500 hover:text-gray-700 font-medium text-sm transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-800 font-medium flex-1">{todo.text}</span>
                      )}
                    </div>

                    {/* Only render action buttons if user is admin AND this item is not currently being edited */}
                    {isOwner && editingId !== todo.id && (
                      <div className="flex space-x-1">
                        <button
                          onClick={() => startEditing(todo)}
                          className="text-gray-400 hover:text-blue-600 p-1 rounded transition-colors"
                          title="Edit task"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => requestDelete(todo.id)}
                          className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                          title="Delete task"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        </section>

        {/* SECTION 2: Tasks Completed */}
        <section className="flex flex-col border rounded-xl bg-gray-50 shadow-sm h-[600px] opacity-90">
          <div className="p-4 border-b bg-gray-100 rounded-t-xl">
            <h2 className="text-lg font-semibold text-gray-600">
              Tasks Completed{" "}
              <span className="text-sm font-normal text-gray-400 ml-1">
                ({completedTodos.length})
              </span>
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-3">
              {completedTodos.length === 0 ? (
                <p className="text-gray-500 italic text-sm text-center mt-4">
                  No completed tasks yet.
                </p>
              ) : (
                completedTodos.map((todo) => (
                  <li
                    key={todo.id}
                    className="flex items-center justify-between p-3 border rounded bg-white"
                  >
                    <div className="flex items-center space-x-3">
                      {isOwner ? (
                        <input
                          type="checkbox"
                          className="h-5 w-5 cursor-pointer text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          checked={todo.isCompleted}
                          onChange={() => handleToggleComplete(todo.id)}
                        />
                      ) : (
                        <span className="text-xl">✅</span>
                      )}
                      <span className="text-gray-400 line-through">{todo.text}</span>
                    </div>

                    {isOwner && (
                      <button
                        onClick={() => requestDelete(todo.id)}
                        className="text-gray-300 hover:text-red-500 p-1 rounded transition-colors"
                        title="Delete task"
                      >
                        🗑️
                      </button>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        </section>
      </div>

      {/* CONFIRMATION MODAL */}
      {todoToDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full transform transition-all">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Task</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this task? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setTodoToDelete(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 font-medium rounded-lg transition-colors"
              >
                Delete Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
